import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { MagnifyingGlass, XCircle, ClockClockwise, Trash, PushPin, PushPinSlash, Globe } from '@phosphor-icons/react';
import { useGlobalPins, addGlobalPin, removeGlobalPin } from '../lib/globalPins';

const STORAGE_PREFIX = 'searchHistory:';
const MAX_RECENT = 5;
const MAX_PINNED = 10;

// Local storage shape: Array<{ q, pinned, ts }> (pinned here = local pin)
const loadHistory = (key) => {
  if (!key || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((it) => {
        if (typeof it === 'string') return { q: it, pinned: false, ts: 0 };
        if (it && typeof it.q === 'string') return { q: it.q, pinned: !!it.pinned, ts: Number(it.ts || 0) };
        return null;
      })
      .filter(Boolean);
  } catch {
    return [];
  }
};

const saveHistory = (key, list) => {
  if (!key || typeof window === 'undefined') return;
  try {
    const pinned = list.filter((it) => it.pinned).slice(0, MAX_PINNED);
    const recent = list.filter((it) => !it.pinned).slice(0, MAX_RECENT);
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify([...pinned, ...recent]));
  } catch {
    /* localStorage unavailable */
  }
};

/**
 * SearchBar with three-tier history:
 *   1. Globally pinned (cloud-synced via /api/me/search-pins, all pages/devices)
 *   2. Locally pinned (this page only, localStorage)
 *   3. Recent unpinned (this page only, last 5)
 *
 * Pin button cycles: unpinned → local-pinned → global-pinned → unpinned
 */
export default function SearchBar({
  value,
  onChange,
  placeholder = 'Search…',
  matchCount,
  totalCount,
  className = '',
  inputClassName = 'pl-8 pr-7 h-8 text-xs',
  noun = 'entries',
  testId = 'search-bar',
  storageKey,
}) {
  const historyKey = storageKey === null || storageKey === false ? null : (storageKey || testId);
  const [history, setHistory] = useState(() => loadHistory(historyKey));
  const globalPins = useGlobalPins();
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapRef = useRef(null);

  // Build a unified list:
  //   - global pins first (sorted by created_at desc)
  //   - then local pins (in stored order)
  //   - then recent unpinned (newest ts first)
  // De-dupe by lowercased query: a query that's globally-pinned hides any local copies.
  const sortedHistory = useMemo(() => {
    const globalQs = new Set(globalPins.map((p) => p.query.toLowerCase()));
    const globalRows = globalPins.map((p) => ({ q: p.query, pinned: 'global', pinId: p.id, ts: p.created_at ? Date.parse(p.created_at) : 0 }));
    const localRows = history
      .filter((it) => !globalQs.has(it.q.toLowerCase()))
      .map((it) => ({ q: it.q, pinned: it.pinned ? 'local' : false, ts: it.ts || 0 }));
    const localPinned = localRows.filter((it) => it.pinned);
    const localRecent = localRows.filter((it) => !it.pinned).sort((a, b) => (b.ts || 0) - (a.ts || 0));
    return [...globalRows, ...localPinned, ...localRecent];
  }, [globalPins, history]);

  const commit = (q) => {
    if (!historyKey) return;
    const trimmedQ = (q || '').trim();
    if (!trimmedQ || trimmedQ.length < 2) return;
    setHistory((prev) => {
      const idx = prev.findIndex((x) => x.q.toLowerCase() === trimmedQ.toLowerCase());
      let next;
      if (idx >= 0) {
        next = prev.map((it, i) => (i === idx ? { ...it, q: trimmedQ, ts: Date.now() } : it));
      } else {
        next = [{ q: trimmedQ, pinned: false, ts: Date.now() }, ...prev];
      }
      saveHistory(historyKey, next);
      return next;
    });
  };

  /** Pin cycle: unpinned → local → global → unpinned */
  const cyclePin = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const q = item.q;
    if (item.pinned === 'global') {
      // Unpin from global
      if (item.pinId) await removeGlobalPin(item.pinId);
      return;
    }
    if (item.pinned === 'local') {
      // Promote local → global, remove from local pins
      const created = await addGlobalPin(q, 'global');
      if (created) {
        setHistory((prev) => {
          const next = prev.filter((x) => x.q.toLowerCase() !== q.toLowerCase());
          saveHistory(historyKey, next);
          return next;
        });
      } else {
        // Fallback: keep as local pin if API failed
      }
      return;
    }
    // Unpinned → local pin
    setHistory((prev) => {
      const idx = prev.findIndex((x) => x.q.toLowerCase() === q.toLowerCase());
      let next;
      if (idx >= 0) {
        next = prev.map((it, i) => (i === idx ? { ...it, pinned: true, ts: it.ts || Date.now() } : it));
      } else {
        next = [{ q, pinned: true, ts: Date.now() }, ...prev];
      }
      saveHistory(historyKey, next);
      return next;
    });
  };

  const removeFromHistory = async (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.pinned === 'global') {
      if (item.pinId) await removeGlobalPin(item.pinId);
      return;
    }
    setHistory((prev) => {
      const next = prev.filter((x) => x.q.toLowerCase() !== item.q.toLowerCase());
      saveHistory(historyKey, next);
      return next;
    });
  };

  const clearAllRecent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only remove unpinned local items; pinned (local + global) stay safe
    setHistory((prev) => {
      const next = prev.filter((it) => it.pinned);
      saveHistory(historyKey, next);
      return next;
    });
  };

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false);
    };
    if (showDropdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const showCount = typeof matchCount === 'number';
  const trimmed = (value || '').trim();
  const showRecentDropdown = showDropdown && historyKey && sortedHistory.length > 0 && !trimmed;
  const hasUnpinned = sortedHistory.some((it) => !it.pinned);

  const pinIcon = (item) => {
    if (item.pinned === 'global') return <Globe size={11} weight="fill" className="text-emerald-600 shrink-0" />;
    if (item.pinned === 'local') return <PushPin size={11} weight="fill" className="text-amber-600 shrink-0" />;
    return <MagnifyingGlass size={11} className="text-gray-400 shrink-0" />;
  };

  const pinTooltip = (item) => {
    if (item.pinned === 'global') return 'Unpin (this is global — across all pages/devices)';
    if (item.pinned === 'local') return 'Promote to global (sync to all pages/devices)';
    return 'Pin to this page';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="relative flex-1 min-w-0 max-w-md" ref={wrapRef}>
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => commit(value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit(value);
              setShowDropdown(false);
            } else if (e.key === 'Escape') {
              setShowDropdown(false);
            }
          }}
          placeholder={placeholder}
          className={inputClassName}
          data-testid={`${testId}-input`}
        />
        {trimmed && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            data-testid={`${testId}-clear`}
            aria-label="Clear search"
          >
            <XCircle size={14} />
          </button>
        )}

        {showRecentDropdown && (
          <div
            className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
            data-testid={`${testId}-history-dropdown`}
          >
            <div className="px-3 py-1.5 bg-gray-50 border-b text-[10px] uppercase font-bold text-gray-500 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <ClockClockwise size={11} weight="duotone" /> Recent &amp; Pinned searches
              </span>
              {hasUnpinned && (
                <button
                  type="button"
                  onMouseDown={clearAllRecent}
                  className="text-[10px] text-gray-400 hover:text-red-600 normal-case"
                  data-testid={`${testId}-history-clear-all`}
                  title="Clear unpinned searches (pinned safe)"
                >
                  Clear recent
                </button>
              )}
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {sortedHistory.map((it, i) => (
                <li key={(it.pinId || it.q) + ':' + i}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(it.q);
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 flex items-center justify-between group ${
                      it.pinned === 'global' ? 'bg-emerald-50/40' : it.pinned === 'local' ? 'bg-amber-50/40' : ''
                    }`}
                    data-testid={`${testId}-history-item-${i}`}
                  >
                    <span className="flex items-center gap-2 truncate">
                      {pinIcon(it)}
                      <span className="truncate">{it.q}</span>
                      {it.pinned === 'global' && (
                        <span className="text-[9px] uppercase font-bold text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded">global</span>
                      )}
                    </span>
                    <span className="flex items-center gap-1 ml-2 shrink-0">
                      <span
                        role="button"
                        tabIndex={-1}
                        onMouseDown={(e) => cyclePin(e, it)}
                        className={`p-0.5 rounded cursor-pointer ${
                          it.pinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        } ${it.pinned === 'global' ? 'text-emerald-600 hover:text-emerald-800' : it.pinned === 'local' ? 'text-amber-600 hover:text-emerald-700' : 'text-gray-400 hover:text-amber-600'}`}
                        title={pinTooltip(it)}
                        aria-label={pinTooltip(it)}
                        data-testid={`${testId}-history-pin-${i}`}
                      >
                        {it.pinned === 'global' ? <PushPinSlash size={11} /> : it.pinned === 'local' ? <Globe size={11} /> : <PushPin size={11} />}
                      </span>
                      <span
                        role="button"
                        tabIndex={-1}
                        onMouseDown={(e) => removeFromHistory(e, it)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 p-0.5 cursor-pointer"
                        aria-label={`Remove ${it.q}`}
                        title="Remove"
                        data-testid={`${testId}-history-remove-${i}`}
                      >
                        <Trash size={11} />
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-3 py-1 bg-gray-50 border-t text-[9px] text-gray-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><Globe size={10} className="text-emerald-600" /> Global = all pages &amp; devices</span>
              <span className="flex items-center gap-1"><PushPin size={10} className="text-amber-600" weight="fill" /> Local = this page only</span>
            </div>
          </div>
        )}
      </div>
      {showCount && (
        <div className="text-[11px] text-gray-500 whitespace-nowrap" data-testid={`${testId}-count`}>
          {trimmed
            ? (typeof totalCount === 'number' ? `${matchCount} of ${totalCount} match` : `${matchCount} match`)
            : `${typeof totalCount === 'number' ? totalCount : matchCount} ${noun}`}
        </div>
      )}
    </div>
  );
}
